import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { Phone } from '@phosphor-icons/react';
import { AlertDialog } from '@astryxdesign/core/AlertDialog';
import { Button } from '@astryxdesign/core/Button';
import { Heading } from '@astryxdesign/core/Heading';
import { Section } from '@astryxdesign/core/Section';
import { StatusLabel } from '@/components/StatusLabel';
import { HStack, VStack } from '@astryxdesign/core/Stack';
import { Text } from '@astryxdesign/core/Text';
import { Skeleton } from '@astryxdesign/core/Skeleton';
import { useToast } from '@astryxdesign/core/Toast';
import { Sheet } from '@/components/Sheet';
import { EmptyView, ErrorView } from '@/components/StateViews';
import { publicApi } from '@/lib/api/public';
import type { BookingView } from '@/lib/api/schemas';
import { ApiError, errorText } from '@/lib/errors';
import { money } from '@/lib/money';
import { forgetBooking, saveBooking, savedBookings, type SavedBooking } from '@/lib/store';
import { dateTimeLabel } from '@/lib/time';
import { ReminderControls } from './ReminderControls';
import { SlotPicker } from './SlotPicker';
import { useBookingRoute, useSlug } from './hooks';

const STATUS: Record<BookingView['status'], { label: string; variant: 'success' | 'warning' | 'neutral' | 'error' | 'accent' }> = {
  confirmed: { label: 'Подтверждена', variant: 'success' },
  arrived: { label: 'Машина в студии', variant: 'accent' },
  completed: { label: 'Работа выполнена', variant: 'neutral' },
  cancelled: { label: 'Отменена', variant: 'error' },
  no_show: { label: 'Визит не состоялся', variant: 'warning' },
};

export function MyBookingsPage() {
  const slug = useSlug();
  const location = useLocation();
  const navigate = useNavigate();
  const { go } = useBookingRoute();
  const [list, setList] = useState<SavedBooking[]>(() => savedBookings(slug));

  // Ссылка вида /s/{slug}/my#t=TOKEN: токен в hash не уходит на сервер и в Referer
  useEffect(() => {
    const m = /^#t=([A-Za-z0-9_-]{40,64})$/.exec(location.hash);
    if (m?.[1]) {
      const token = m[1];
      if (!savedBookings(slug).some((b) => b.token === token)) {
        saveBooking(slug, { token, startsAt: '', serviceName: '', savedAt: new Date().toISOString() });
      }
      setList(savedBookings(slug));
      navigate({ pathname: location.pathname, hash: '' }, { replace: true });
    }
  }, [location.hash, location.pathname, navigate, slug]);

  const queries = useQueries({
    queries: list.map((b) => ({
      queryKey: ['booking', b.token],
      queryFn: () => publicApi.booking(b.token),
      retry: (n: number, e: Error) => n < 2 && !(e instanceof ApiError && e.code === 'invalid_token'),
    })),
  });

  if (list.length === 0) {
    return (
      <Section variant="transparent" padding={4} paddingBlockStart={6}>
        <Heading level={1}>Моя запись</Heading>
        <EmptyView
          title="Здесь появятся ваши записи"
          description="Запишитесь онлайн — запись сохранится на этом устройстве. Если запись была сделана на другом телефоне, откройте ссылку из неё."
          action={<Button label="Записаться" variant="primary" onClick={() => go('service')} />}
        />
      </Section>
    );
  }

  return (
    <Section variant="transparent" padding={4} paddingBlockStart={6}>
      <VStack gap={4}>
        <Heading level={1}>Моя запись</Heading>
        {list.map((saved, i) => {
          const q = queries[i]!;
          if (q.isPending) return <Skeleton key={saved.token} height={180} radius="rounded" index={i} />;
          if (q.isError) {
            const invalid = q.error instanceof ApiError && q.error.code === 'invalid_token';
            return invalid ? (
              <HStack key={saved.token} justify="between" align="center" gap={3}>
                <Text color="secondary">Запись по сохранённой ссылке не найдена.</Text>
                <Button
                  label="Убрать"
                  variant="ghost"
                  onClick={() => {
                    forgetBooking(slug, saved.token);
                    setList(savedBookings(slug));
                  }}
                />
              </HStack>
            ) : (
              <ErrorView key={saved.token} error={q.error} onRetry={() => void q.refetch()} />
            );
          }
          return <BookingCard key={saved.token} booking={q.data} token={saved.token} />;
        })}
        <Button label="Записаться ещё" onClick={() => go('service')} />
      </VStack>
    </Section>
  );
}

function BookingCard({ booking: b, token }: { booking: BookingView; token: string }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [reschedule, setReschedule] = useState(false);
  const [newTime, setNewTime] = useState<string | null>(null);
  const tz = b.tenant.timezone;
  const status = STATUS[b.status];
  const past = Date.parse(b.starts_at) < Date.now();

  const update = (next: BookingView) => qc.setQueryData(['booking', token], next);

  const cancel = useMutation({
    mutationFn: () => publicApi.cancel(token, ''),
    onSuccess: (next) => {
      update(next);
      setConfirmCancel(false);
      void qc.invalidateQueries({ queryKey: ['slots', b.tenant.slug] });
      toast({ body: 'Запись отменена' });
    },
    onError: (e) => toast({ body: errorText(e), type: 'error' }),
  });

  const move = useMutation({
    mutationFn: (at: string) => publicApi.reschedule(token, at),
    onSuccess: (next) => {
      update(next);
      setReschedule(false);
      setNewTime(null);
      void qc.invalidateQueries({ queryKey: ['slots', b.tenant.slug] });
      toast({ body: `Запись перенесена на ${dateTimeLabel(next.starts_at, tz)}` });
    },
    onError: (e) => {
      void qc.invalidateQueries({ queryKey: ['slots', b.tenant.slug] });
      toast({ body: `${errorText(e)} Исходная запись сохранена.`, type: 'error' });
    },
  });

  return (
    <article className="rounded-lg border border-border bg-surface" aria-label={`Запись: ${b.service_name}`}>
      <VStack gap={4} padding={4}>
        <VStack gap={1}>
          <StatusLabel variant={status.variant} label={status.label} />
          <Text type="large" weight="semibold">
            {dateTimeLabel(b.starts_at, tz)}
          </Text>
          <Text color="secondary">
            {b.service_name} · {b.resource_name} · {money(b.price_cents, 'RUB', b.price_is_from)}
          </Text>
          <Text color="secondary">
            {b.car}
            {b.tenant.address ? ` · ${b.tenant.address}` : ''}
          </Text>
          {b.is_demo ? <Text type="supporting" color="secondary">Тестовая запись демо-режима</Text> : null}
        </VStack>

        {b.status === 'confirmed' && !past ? <ReminderControls booking={b} token={token} /> : null}

        {b.status === 'confirmed' && !past ? (
          b.can_change ? (
            <HStack gap={2}>
              <Button label="Перенести" width="100%" onClick={() => setReschedule(true)} />
              <Button label="Отменить" variant="destructive" width="100%" onClick={() => setConfirmCancel(true)} />
            </HStack>
          ) : (
            <Text type="supporting" color="secondary">
              Онлайн менять запись уже нельзя: осталось меньше {Math.round(b.tenant.cancel_cutoff_minutes / 60)} ч. Позвоните в студию.
            </Text>
          )
        ) : null}

        {b.tenant.phone ? (
          <Button label="Позвонить в студию" variant="ghost" href={`tel:${b.tenant.phone}`} icon={<Phone size={18} />} />
        ) : null}
      </VStack>

      <AlertDialog
        isOpen={confirmCancel}
        onOpenChange={setConfirmCancel}
        title="Отменить запись?"
        description={`${b.service_name}, ${dateTimeLabel(b.starts_at, tz)}. Время освободится для других клиентов.`}
        actionLabel="Отменить запись"
        cancelLabel="Оставить"
        isActionLoading={cancel.isPending}
        onAction={() => cancel.mutate()}
      />

      <Sheet
        open={reschedule}
        onOpenChange={(o) => {
          setReschedule(o);
          if (!o) setNewTime(null);
        }}
        title="Новое время"
        description={b.service_name}
        focusKey={reschedule ? 'reschedule' : undefined}
        footer={
          <Button
            label={newTime ? `Перенести на ${dateTimeLabel(newTime, tz)}` : 'Выберите время'}
            variant="primary"
            size="lg"
            width="100%"
            isDisabled={!newTime}
            isLoading={move.isPending}
            onClick={() => newTime && move.mutate(newTime)}
          />
        }
      >
        {reschedule ? (
          <SlotPicker slug={b.tenant.slug} tz={tz} serviceId={b.service_id} value={newTime} onChange={setNewTime} exclude={b.starts_at} />
        ) : null}
      </Sheet>
    </article>
  );
}
